/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState, useLayoutEffect, type PropsWithChildren } from 'react';
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { RightPanel } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { previewPanelStateResource } from '@jira/navigation-apps__resources/src/services/preview-panel/index.tsx';
import { useLayoutStoreActions } from '@jira/platform__controllers-layout/src/controllers/layout-controller/store/index.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import {
	PanelSplitter,
	type ResizeBounds,
} from '@atlaskit/navigation-system/layout/panel-splitter';

import { jsx, cssMap } from '@atlaskit/css';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, defineMessage } from '@atlassian/jira-intl/src/index.tsx';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { PreviewPanelQuery } from './__generated__/PreviewPanelQuery.graphql.ts';
import { PreviewPanelWidthContext } from './PreviewPanelWidthContext.tsx';
import { useIssuePreviewPanelsNavigation } from './useIssuePreviewPanelsNavigation.tsx';

export const usePreviewPanelWidth = (
	queryReference: PreloadedQuery<PreviewPanelQuery>,
): number | undefined => {
	const data = usePreloadedQuery<PreviewPanelQuery>(
		graphql`
			query PreviewPanelQuery($cloudId: ID!) {
				jira {
					navigationUIState(cloudId: $cloudId) {
						previewPanel {
							width
						}
					}
				}
			}
		`,
		queryReference,
	);

	return data?.jira?.navigationUIState?.previewPanel?.width ?? undefined;
};

const previewPanelSplitterLabel = defineMessage({
	id: 'page-container-v2.common.preview-panel.preview-panel-splitter-label',
	defaultMessage: 'Resize preview panel',
	description: 'Button label for resizing the preview panel',
});

const styles = cssMap({
	clickCapture: {
		display: 'contents',
	},
});

const convertMaxWidthToPixels = (maxWidth: ResizeBounds['max']): number =>
	maxWidth.endsWith('vw')
		? Math.floor(((globalThis.window?.innerWidth ?? 0) * parseInt(maxWidth, 10)) / 100)
		: Math.floor(parseInt(maxWidth, 10));

const MaybeMarkPreviewPanelClick = functionWithCondition(
	() => fg('jivtech-1920-back-navigation-gpp'),
	({ children }: PropsWithChildren<{}>) => {
		const { markPreviewPanelClick } = useIssuePreviewPanelsNavigation();
		return (
			<div css={styles.clickCapture} onClickCapture={markPreviewPanelClick}>
				{children}
			</div>
		);
	},
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
);

const PreviewPanelInternal = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<PreviewPanelQuery>;
}) => {
	const panel = useCurrentPreviewPanel();
	const { formatMessage } = useIntl();
	const [, { setPreviewPanelState }] = useLayoutStoreActions();
	const [useLocalWidth, setUseLocalWidth] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onResizeEnd = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			panel?.setWidth?.(finalWidth);
			setPreviewPanelState({
				width: finalWidth,
			});
			setUseLocalWidth(true);

			const maxWidth = panel?.maxWidth;

			fireUIAnalytics(createAnalyticsEvent({}), 'preview-panel resized', {
				panelId: panel?.id,
				panelType: panel?.type,
				initialWidth,
				finalWidth,
				...(maxWidth != null &&
					expVal('nin_adaptive_preview_panel', 'isEnabled', false) && {
						isMaxWidth: finalWidth >= convertMaxWidthToPixels(maxWidth) - 1,
					}),
			});
		},
		[panel, setPreviewPanelState, createAnalyticsEvent],
	);

	const widthFromQuery = usePreviewPanelWidth(queryReference);

	useLayoutEffect(() => {
		if (panel && !useLocalWidth && widthFromQuery != null) {
			panel.setWidth?.(widthFromQuery);
		}
	}, [panel, useLocalWidth, widthFromQuery]);

	if (panel === null && !fg('platform-dst-motion-uplift-panel')) {
		return null;
	}

	const PanelContent = panel?.content ?? null;

	const previewPanelWidth = useLocalWidth ? panel?.width : (widthFromQuery ?? panel?.width);
	const wrappedPanelContent = fg('jfp-magma-fix-streaming-nin-issue-preview-panel') ? (
		<PreviewPanelWidthContext.Provider value={previewPanelWidth}>
			<MaybeMarkPreviewPanelClick>{PanelContent && <PanelContent />}</MaybeMarkPreviewPanelClick>
		</PreviewPanelWidthContext.Provider>
	) : (
		<MaybeMarkPreviewPanelClick>{PanelContent && <PanelContent />}</MaybeMarkPreviewPanelClick>
	);

	if (fg('platform-dst-motion-uplift-panel')) {
		return (
			<ExitingPersistence appear>
				{panel !== null && PanelContent && (
					<RightPanel
						key="preview-panel"
						width={previewPanelWidth}
						testId="preview-panels.preview-panel"
						maxWidth={panel.maxWidth}
					>
						{wrappedPanelContent}
						<PanelSplitter
							label={formatMessage(previewPanelSplitterLabel)}
							onResizeEnd={onResizeEnd}
							testId="preview-panels.panel-splitter"
						/>
					</RightPanel>
				)}
			</ExitingPersistence>
		);
	}

	return (
		<RightPanel
			width={previewPanelWidth}
			testId="preview-panels.preview-panel"
			maxWidth={panel?.maxWidth}
		>
			{wrappedPanelContent}
			<PanelSplitter
				label={formatMessage(previewPanelSplitterLabel)}
				onResizeEnd={onResizeEnd}
				testId="preview-panels.panel-splitter"
			/>
		</RightPanel>
	);
};

const PreviewPanelWithoutWidthPersistance = () => {
	const panel = useCurrentPreviewPanel();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onResizeEnd = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			panel?.setWidth?.(finalWidth);

			const maxWidth = panel?.maxWidth;

			fireUIAnalytics(createAnalyticsEvent({}), 'preview-panel resized', {
				panelId: panel?.id,
				panelType: panel?.type,
				initialWidth,
				finalWidth,
				...(maxWidth != null &&
					expVal('nin_adaptive_preview_panel', 'isEnabled', false) && {
						isMaxWidth: finalWidth >= convertMaxWidthToPixels(maxWidth) - 1,
					}),
			});
		},
		[panel, createAnalyticsEvent],
	);

	if (panel === null && !fg('platform-dst-motion-uplift-panel')) {
		return null;
	}

	const PanelContent = panel?.content ?? null;

	const previewPanelWidth = panel?.width;

	if (fg('platform-dst-motion-uplift-panel')) {
		return (
			<ExitingPersistence appear>
				{panel !== null && PanelContent && (
					<RightPanel
						key="preview-panel"
						width={previewPanelWidth}
						maxWidth={panel.maxWidth}
						testId="preview-panels.preview-panel"
					>
						{PanelContent && <PanelContent />}
						<PanelSplitter
							label={formatMessage(previewPanelSplitterLabel)}
							onResizeEnd={onResizeEnd}
							testId="preview-panels.panel-splitter"
						/>
					</RightPanel>
				)}
			</ExitingPersistence>
		);
	}

	return (
		<RightPanel
			width={previewPanelWidth}
			maxWidth={panel?.maxWidth}
			testId="preview-panels.preview-panel"
		>
			{PanelContent && <PanelContent />}
			<PanelSplitter
				label={formatMessage(previewPanelSplitterLabel)}
				onResizeEnd={onResizeEnd}
				testId="preview-panels.panel-splitter"
			/>
		</RightPanel>
	);
};

const PreviewPanelFallback = () => <PreviewPanelWithoutWidthPersistance />;

export const PreviewPanel = (): React.JSX.Element | null => {
	const { queryReference } = useRelayResource(previewPanelStateResource);

	if (!queryReference) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="preview-panel-width-query"
			packageName="preview-panels"
			teamName="Gryffindor"
			fallback={PreviewPanelFallback}
		>
			<Placeholder name="preview-panel-width-query" fallback={<PreviewPanelFallback />}>
				<PreviewPanelInternal queryReference={queryReference} />
			</Placeholder>
		</JSErrorBoundary>
	);
};
